import React, {
  DetailedHTMLProps,
  HTMLAttributes,
  useEffect,
  useState,
} from 'react';
import { Stack, Typography } from '@mui/material';

import { UploadWidgetStyled, ContainerStyled } from './upload-widget.styled';
import { IconSize } from '@constants';
import { useTranslation } from 'react-i18next';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { ThumbnailImageUploadViewerProps } from '@atoms/ThumbnailImageUploadViewer';

interface ImageUploadProps {
  onImageUpload: (file: File) => void;
  value?: File;
  isSelected?: boolean;
  urlValue?: string;

  ComponentUpload?: React.FC<
    DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>
  >;
  ComponentView?: React.FC<ThumbnailImageUploadViewerProps>;
}

const maxSize = 20 * 1024 * 1024; // 20MB in bytes

const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageUpload,
  value,
  ComponentUpload,
  ComponentView,
  isSelected,
  urlValue,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | undefined>(
    value ? URL.createObjectURL(value) : undefined,
  );

  const { t } = useTranslation();

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];

    const allowedFormats = ['image/jpeg', 'image/png'];
    if (!file) return;
    if (file.size >= maxSize) return;
    if (!allowedFormats.includes(file.type)) return;

    if (file) onImageUpload(file);
    setImageUrl(URL.createObjectURL(file));
  };

  const handleClick = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (!file) return;
      if (file.size >= maxSize) return;

      if (file) onImageUpload(file);
      setImageUrl(URL.createObjectURL(file));
    };
    input.click();
  };

  useEffect(() => {
    if (urlValue && !imageUrl) setImageUrl(urlValue);
  }, [urlValue]);
  if (ComponentUpload && ComponentView)
    return (
      <ComponentUpload
        data-qa={`image-upload`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {imageUrl && (
          <ComponentView
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={handleClick}
            imageUrl={imageUrl}
            isSelected={!!isSelected}
          />
        )}
      </ComponentUpload>
    );
  return (
    <ContainerStyled>
      <UploadWidgetStyled
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {imageUrl ? (
          <img src={imageUrl} alt="Uploaded" />
        ) : (
          <Stack alignContent={'center'} gap={2}>
            <ArrowUpTrayIcon height={IconSize.md} />
            <Typography variant="caption">
              {isDragging ? '... ' : t('DRAG_IMAGE_OR_IMPORT_IT')}
            </Typography>
          </Stack>
        )}
      </UploadWidgetStyled>
    </ContainerStyled>
  );
};

export default ImageUpload;
